import {
  AlertCircle,
  Check,
  Circle,
  Clock3,
  Copy,
  RefreshCw,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useProjectOverviewQuery, type ProjectOverview } from "@/api/hooks";
import { useRunValidation } from "@/api/validation";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import { journeyDeadline } from "@/lib/journeyFreshness";
import { lifecycleRefusalCopy } from "@/lib/lifecycle";
import { Button } from "@/ui/Button";
import { Card, CardBody } from "@/ui/Card";
import { PublicationHandoffDialog } from "@/ui/publication/PublicationHandoffDialog";
import { ValidationPanel } from "@/ui/validation/ValidationPanel";

type Journey = NonNullable<ProjectOverview["journey"]>;
type StageState = Journey["stages"][number]["state"];

const STAGE_STYLE = {
  completed: "border-emerald-600 bg-emerald-50 text-emerald-800",
  current:
    "border-primary bg-primary text-primary-foreground ring-4 ring-primary/15",
  upcoming: "border-border bg-card text-muted-foreground",
  blocked: "border-amber-600 bg-amber-50 text-amber-900",
  failed: "border-destructive bg-destructive/10 text-destructive",
  previous: "border-border bg-muted text-muted-foreground",
} satisfies Record<StageState, string>;

const STATE_LABEL = {
  completed: "Completed",
  current: "Current stage",
  upcoming: "Next",
  blocked: "Waiting / blocked",
  failed: "Needs recovery",
  previous: "Previous",
} satisfies Record<StageState, string>;

/** Display only: ordering, status, action and refusal all come from the server. */
export function JourneyTimeline({ journey }: { journey: Journey }) {
  return (
    <ol
      aria-label="Project lifecycle progress"
      className={cn("grid gap-3 xl:gap-2", journey.developmentDelivery ? "xl:grid-cols-10" : "xl:grid-cols-9")}
    >
      {journey.stages.map((stage, index) => {
        const Icon =
          stage.state === "completed"
            ? Check
            : stage.state === "failed"
              ? AlertCircle
              : stage.state === "blocked"
                ? Clock3
                : Circle;
        return (
          <li
            key={stage.id}
            aria-current={
              stage.id === journey.currentStage && stage.state !== "previous"
                ? "step"
                : undefined
            }
            className="relative flex min-w-0 items-center gap-3 xl:flex-col xl:items-start"
          >
            {index < journey.stages.length - 1 ? (
              <span
                aria-hidden="true"
                className="absolute start-[17px] top-9 h-3 w-px bg-border xl:start-9 xl:top-[17px] xl:h-px xl:w-[calc(100%-1.75rem)]"
              />
            ) : null}
            <span
              className={cn(
                "relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full border-2",
                STAGE_STYLE[stage.state] ?? STAGE_STYLE.blocked,
              )}
            >
              <Icon aria-hidden="true" className="size-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-snug">
                {stage.label}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {STATE_LABEL[stage.state] ?? "Unrecognized state"}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function ProjectJourney({ projectId }: { projectId: string }) {
  const overview = useProjectOverviewQuery(projectId);
  const refetch = overview.refetch;
  const journey = overview.data?.journey;
  const navigate = useNavigate();
  const validation = useRunValidation(projectId);
  const receivedAt = overview.dataUpdatedAt;
  const expiresAt = journeyDeadline(journey, receivedAt);
  const [expiredSnapshot, setExpiredSnapshot] = useState<number | null>(() =>
    Date.now() >= expiresAt ? receivedAt : null,
  );
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [handoff, setHandoff] = useState<Journey["publication"]>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setExpiredSnapshot(receivedAt),
      Math.max(0, expiresAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt, receivedAt]);
  useEffect(() => {
    const refreshVisible = () => {
      if (document.visibilityState === "visible") {
        if (Date.now() >= expiresAt) setExpiredSnapshot(receivedAt);
        void refetch();
      }
    };
    const timer = window.setInterval(refreshVisible, 30_000);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [refetch, expiresAt, receivedAt]);

  const stale =
    overview.isError ||
    !journey ||
    expiredSnapshot === receivedAt ||
    expiresAt === 0;
  const action = journey?.nextAction;
  const actionUnavailable =
    stale || !action?.enabled || confirming || validation.isRunning;
  const base = `/projects/${encodeURIComponent(projectId)}`;

  async function refresh() {
    setNotice(null);
    await overview.refetch();
  }

  async function act() {
    if (actionUnavailable || !journey) return;
    setNotice(null);
    setConfirming(true);
    const fresh = await overview.refetch().catch(() => null);
    setConfirming(false);
    const confirmed = fresh?.data?.journey;
    if (
      !fresh ||
      fresh.isError ||
      !confirmed ||
      Date.now() >= journeyDeadline(confirmed, fresh.dataUpdatedAt)
    ) {
      setNotice(
        "Progress could not be confirmed. Check again before continuing.",
      );
      return;
    }
    if (
      !confirmed.nextAction.enabled ||
      confirmed.nextAction.kind !== journey.nextAction.kind ||
      confirmed.candidate?.identity.candidateId !==
        journey.candidate?.identity.candidateId ||
      confirmed.publication?.approvalId !== journey.publication?.approvalId ||
      confirmed.publication?.finalHeadSha !== journey.publication?.finalHeadSha
    ) {
      setNotice(
        "The Project changed. Review the refreshed journey before continuing.",
      );
      return;
    }
    switch (confirmed.nextAction.kind) {
      case "validate":
        setShowValidation(true);
        await validation.run();
        break;
      case "freeze":
      case "preview":
      case "accept":
        void navigate(`${base}/preview`);
        break;
      case "request_publication":
        void navigate(`${base}/submit`);
        break;
      case "request_promotion":
        if (confirmed.publication?.bindable) setHandoff(confirmed.publication);
        break;
      case "check_again":
      case "none":
        break;
      default:
        setNotice(
          "This step needs a newer version of the Editor. Reload the page to continue.",
        );
    }
  }

  async function copyPr() {
    if (!journey?.publication?.prUrl || stale) return;
    try {
      await navigator.clipboard.writeText(journey.publication.prUrl);
      setNotice("Clinical Pack PR link copied.");
    } catch {
      setNotice(
        "The PR link could not be copied. It is available in Technical evidence.",
      );
    }
  }

  return (
    <section aria-labelledby="project-journey-heading" className="space-y-3">
      <div>
        <h2 id="project-journey-heading" className="text-lg font-semibold">
          From draft to release
        </h2>
        <p className="text-sm text-muted-foreground">
          The journey of this Project’s current configuration.
        </p>
      </div>
      <Card className="overflow-hidden border-primary/25">
        <CardBody className="space-y-6 p-5 sm:p-6">
          {stale ? (
            <div
              role="status"
              className="rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-950"
            >
              <p>
                Current progress is unavailable. Check again to confirm this
                Project’s state.
              </p>
              <Button
                variant="outline"
                className="mt-3"
                onClick={() => void refresh()}
              >
                Check again
              </Button>
            </div>
          ) : journey ? (
            <JourneyTimeline journey={journey} />
          ) : null}

          {!stale && journey && action ? (
            <div className="flex flex-col gap-4 rounded-lg bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Next step
                </p>
                <p role="status" className="text-sm leading-relaxed">
                  {journey.message}
                </p>
                {action.reason ? (
                  <p className="text-sm text-muted-foreground">
                    {lifecycleRefusalCopy(action.reason)}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {action.kind !== "none" ? (
                  <Button
                    disabled={
                      actionUnavailable ||
                      (action.kind === "request_promotion" &&
                        submitted === journey.publication?.publicationId)
                    }
                    onClick={() => void act()}
                    className="motion-reduce:transition-none"
                  >
                    {action.kind === "check_again" ? (
                      <RefreshCw aria-hidden="true" className="size-4" />
                    ) : null}
                    {confirming
                      ? "Checking…"
                      : validation.isRunning
                        ? "Validating…"
                        : action.label}
                  </Button>
                ) : null}
                {journey.publication?.prUrl ? (
                  <Button variant="outline" onClick={() => void copyPr()}>
                    <Copy aria-hidden="true" className="size-4" />
                    Copy PR link
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          {notice ? (
            <p role="status" className="text-sm">
              {notice}
            </p>
          ) : null}
          {journey?.previousVersion ? (
            <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              {journey.previousVersion}
            </p>
          ) : null}
          <div className="space-y-2 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
            <p>
              Clinical sign-off means “Accept this preview.” Request publication
              creates a Clinical Pack PR. A different reviewer approves it in
              GitHub. {journey?.developmentDelivery
                ? "After merge, the approved pack deploys automatically to the accepted target in this environment."
                : "PR merge publishes the Clinical Pack. Request promotion records the release handoff."}
            </p>
            <p>
              {journey?.deploymentNote ??
                "Deployment is not reported here. The release team verifies the exact pack pin and deployment health."}
            </p>
          </div>
          {journey?.developmentDelivery?.observedAt ? (
            <p className="text-xs text-muted-foreground">
              Last deployment observation: {formatDateTime(journey.developmentDelivery.observedAt)}
            </p>
          ) : null}
          {journey?.developmentDelivery?.runUrl ? (
            <a href={journey.developmentDelivery.runUrl} target="_blank" rel="noreferrer"
              className="text-sm underline">View deployment run</a>
          ) : null}
          {journey?.developmentDelivery?.patientUrl ? (
            <a href={journey.developmentDelivery.patientUrl} target="_blank" rel="noreferrer"
              className="text-sm underline">Open live patient site</a>
          ) : null}
          {journey ? <JourneyEvidence journey={journey} /> : null}
        </CardBody>
      </Card>
      {showValidation ? <ValidationPanel projectId={projectId} /> : null}
      {!stale &&
      handoff &&
      journey?.nextAction.kind === "request_promotion" &&
      journey.nextAction.enabled &&
      journey.publication?.bindable &&
      journey.publication?.approvalId === handoff.approvalId &&
      journey.publication?.finalHeadSha === handoff.finalHeadSha ? (
        <PublicationHandoffDialog
          projectId={projectId}
          evidence={handoff}
          onClose={() => setHandoff(null)}
          onCompleted={() => {
            setSubmitted(handoff.publicationId);
            setHandoff(null);
            void refresh();
          }}
          onPreconditionMoved={() => {
            setHandoff(null);
            setNotice("The approval changed. Check the refreshed journey.");
            void overview.refetch();
          }}
        />
      ) : null}
    </section>
  );
}

function JourneyEvidence({ journey }: { journey: Journey }) {
  const rows = [
    ["Snapshot", formatDateTime(journey.asOf)],
    ["Candidate", journey.candidate?.identity.candidateId],
    ["Content hash", journey.candidate?.identity.contentHash],
    ["Base pack SHA", journey.candidate?.identity.basePackSha],
    ["Revision", journey.candidate?.revision.number],
    [
      "Validity ends",
      journey.candidate
        ? formatDateTime(journey.candidate.validity.expiresAt)
        : null,
    ],
    ["Repository", journey.publication?.repository],
    ["Clinical Pack PR", journey.publication?.prUrl],
    ["Observed final head", journey.publication?.finalHeadSha],
    ["Approved head", journey.publication?.approvedHeadSha],
    ["Merge commit", journey.publication?.mergeCommitSha],
    [
      "GitHub last observed",
      journey.publication?.lastObservedAt
        ? formatDateTime(journey.publication.lastObservedAt)
        : null,
    ],
    ["Recorded promotion handoff", journey.handoffId],
  ];
  return (
    <details>
      <summary className="cursor-pointer text-sm font-medium text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring">
        Technical evidence
      </summary>
      <dl className="mt-3 grid gap-3 rounded-md bg-muted/40 p-3 text-xs sm:grid-cols-2">
        {rows
          .filter(([, value]) => value != null)
          .map(([label, value]) => (
            <div key={label}>
              <dt className="font-semibold text-muted-foreground">{label}</dt>
              <dd className="mt-1 select-all break-all font-mono">{value}</dd>
            </div>
          ))}
      </dl>
    </details>
  );
}
